import { useFilters } from '@statsbomb/kitbag-global-football';
import { KeysToCamelCase, Cycle } from '@statsbomb/parachute-types';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { missing360DataBannerCyclesMessageAtom } from '@/atoms/dataLocker/general';
import { competitionLookupAtom } from '@/atoms/queries/competitions';
import { metricKeyIs360MapAtom } from '@/atoms/queries/metric';
import { seasonLookupAtom } from '@/atoms/queries/seasons';
import { sortCompetitionSeasonNamesBySeasonDescCompAsc } from '@/utils/cycles';
import { getMetricBaseName } from '@/utils/metrics';
import { useSelectedCyclesMatching360Check } from './useSelectedCyclesMatching360Check/useSelectedCyclesMatching360Check';

const COMPETITIONS_SEASONS_DISPLAY_LIMIT = 5;

/**
 * This hook gets the selected 360 metrics from the selected metric distributions.
 * Note that a selected metric only counts here if the either the min or max has been set
 * to something other than undefined.
 *
 * You might wonder why we don't make this a derived atom instead. The reason is that
 * I'm trying to minimise the amount of times we have to re-run this logic, and
 * using a hook that we call only when we need to set the banner message means we
 * only run this logic when necessary.
 */
const use360SelectedMetrics = () => {
  const [{ metricDistributions: selectedMetricDistributions }] = useFilters('main');
  const metricKeyIs360Map = useAtomValue(metricKeyIs360MapAtom);

  return () =>
    selectedMetricDistributions.reduce<string[]>((acc, { min, max, name }) => {
      if (min === undefined && max === undefined) return acc;
      const key = getMetricBaseName(name);
      return metricKeyIs360Map[key] ? [...acc, key] : acc;
    }, []);
};

/**
 * This hook gets the sorted competition and season names for the given non-360 selected cycles.
 * The names are sorted by season name (descending) and then competition name (ascending).
 */
const useSortedCompetitionSeasonNames = () => {
  const competitionLookup = useAtomValue(competitionLookupAtom);
  const seasonLookup = useAtomValue(seasonLookupAtom);

  return (non360SelectedCycles: KeysToCamelCase<Cycle>[]) => {
    const competitionSeasonNames = non360SelectedCycles.map(({ competitionId, seasonId }) => ({
      competitionName: competitionLookup.get(competitionId)?.name || '',
      seasonName: seasonLookup.get(seasonId)?.name || '',
    }));
    const sortedNames = sortCompetitionSeasonNamesBySeasonDescCompAsc(competitionSeasonNames);
    return sortedNames.map(({ competitionName, seasonName }) => `${competitionName} ${seasonName}`);
  };
};

/**
 * The job of this hook is to construct the message that we show in the banner
 */
const useBuildMissing360DataBannerCyclesMessage = () => {
  const { t } = useTranslation('general');
  const get360SelectedMetrics = use360SelectedMetrics();
  const getNon360SelectedCycles = useSelectedCyclesMatching360Check(false);
  const getSortedCompetitionSeasonNames = useSortedCompetitionSeasonNames();

  return () => {
    const selected360Metrics = get360SelectedMetrics();
    const non360SelectedCycles = getNon360SelectedCycles();
    if (selected360Metrics.length === 0 || non360SelectedCycles.length === 0) return '';

    const formattedCompSeasonNames = getSortedCompetitionSeasonNames(non360SelectedCycles)
      .slice(0, COMPETITIONS_SEASONS_DISPLAY_LIMIT)
      .join(', ');

    const showPlusMoreText = non360SelectedCycles.length > COMPETITIONS_SEASONS_DISPLAY_LIMIT;

    return `${formattedCompSeasonNames}${
      showPlusMoreText
        ? ` ${t('plusMore', { count: non360SelectedCycles.length - COMPETITIONS_SEASONS_DISPLAY_LIMIT })}`
        : ''
    }.`;
  };
};

/**
 * This hook is responsible for building a message that we show in the banner above the data table on data lockers.
 * The message is set if the user has selected 360 metrics but has also selected competitions/seasons that do not have 360 data.
 *
 * It uses a series of helper hooks in an attempt to keep the responsibility of each part clear.
 */
export const useSetMissing360DataBannerMessage = () => {
  const buildMissing360DataBannerCyclesMessage = useBuildMissing360DataBannerCyclesMessage();
  const setMissing360DataBannerCycles = useSetAtom(missing360DataBannerCyclesMessageAtom);

  return () => setMissing360DataBannerCycles(buildMissing360DataBannerCyclesMessage());
};
